/**
 * MonogramPreviewModal — instant Google-Fonts based monogram previews.
 * Renders 20+ font styles on the buyer's device (no API round-trip), plus a
 * curated thread-shade chart. Buyer picks a font + thread colour and the
 * selection flows into the variation state / cart line item.
 *
 * Falls back gracefully if a font family is still loading.
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, Palette } from "lucide-react";
import { toast } from "sonner";
import { MONOGRAM_FONTS, THREAD_SHADES } from "@/data/monogramFonts";

export const MonogramPreviewModal = ({ open, defaultLetters = "", defaultThread = "", onClose, onSelect }) => {
  const [letters, setLetters] = useState("");
  const [chosen, setChosen] = useState(null); // font style object
  const [thread, setThread] = useState(null); // shade object
  const [customThread, setCustomThread] = useState("");
  const [family, setFamily] = useState("All");

  useEffect(() => {
    if (open) {
      setLetters((defaultLetters || "").trim().toUpperCase().slice(0, 6));
      setChosen(null);
      setThread(null);
      setCustomThread(defaultThread || "");
      setFamily("All");
      // Lazy-load the ~40 monogram Google Fonts only when the modal first opens.
      // This keeps them out of the critical render path — huge Lighthouse win.
      if (typeof document !== "undefined" && !document.getElementById("anabiya-monogram-fonts")) {
        const link = document.createElement("link");
        link.id = "anabiya-monogram-fonts";
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Alex+Brush&family=Dancing+Script:wght@400;700&family=Pinyon+Script&family=Allura&family=Sacramento&family=Cinzel:wght@400;600;800&family=Cinzel+Decorative:wght@400;700;900&family=Cormorant+SC:wght@400;600&family=UnifrakturCook:wght@700&family=IM+Fell+English+SC&family=IM+Fell+French+Canon+SC&family=Marcellus+SC&family=Marcellus&family=Cardo:wght@400;700&family=Old+Standard+TT:wght@400;700&family=Josefin+Sans:wght@300;500&family=Bebas+Neue&family=Abril+Fatface&family=Yeseva+One&family=Italiana&family=Tangerine:wght@400;700&family=Petit+Formal+Script&family=Style+Script&family=Parisienne&family=Kaushan+Script&family=Lobster&family=Pacifico&family=Yellowtail&family=Berkshire+Swash&family=Mrs+Saint+Delafield&family=Herr+Von+Muellerhoff&family=Cookie&family=Meddon&family=Rouge+Script&family=League+Script&family=Monsieur+La+Doulaise&family=MedievalSharp&family=Bilbo+Swash+Caps&family=Dawning+of+a+New+Day&family=Ephesis&display=swap";
        document.head.appendChild(link);
      }
    }
  }, [open, defaultLetters, defaultThread]);

  const previewLetters = (letters || "ABC").trim() || "ABC";

  const families = useMemo(() => {
    const set = new Set(THREAD_SHADES.map((s) => s.family));
    return ["All", ...Array.from(set)];
  }, []);
  const filteredShades = useMemo(
    () => (family === "All" ? THREAD_SHADES : THREAD_SHADES.filter((s) => s.family === family)),
    [family]
  );

  const confirm = () => {
    if (!chosen) return toast.error("Pick a font style first");
    const threadName = customThread.trim() || thread?.name;
    if (!threadName) return toast.error("Pick a thread colour (or type your own)");
    onSelect?.({
      monogramLetters: previewLetters,
      monogramStyle: chosen.label,
      monogramFont: chosen.id,
      monogramFontFamily: chosen.family,
      monogramThread: threadName + (thread?.code ? ` (${thread.code})` : ""),
      monogramThreadHex: thread?.hex || "",
      // Legacy image URL kept for compatibility; empty since preview is CSS-based.
      monogramImage: "",
    });
    toast.success("Monogram saved to your order");
    onClose?.();
  };

  const chosenColour = thread?.hex || "#1B4332"; // preview colour

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-forest/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
          data-testid="monogram-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-5xl max-h-[92vh] overflow-y-auto border border-hairline"
          >
            {/* Header */}
            <header className="sticky top-0 bg-white border-b border-hairline px-6 md:px-8 py-5 flex items-center justify-between z-10">
              <div>
                <div className="overline text-gold text-[10px] tracking-[3px]">§ Personalise</div>
                <h3 className="font-display text-forest text-2xl md:text-3xl mt-1 leading-none flex items-center gap-2">
                  <Sparkles size={18} strokeWidth={1.6} className="text-gold" />
                  Select font style for your monogram
                </h3>
                <p className="text-forest/55 text-xs mt-1">Type 3–6 letters. Pick a font style and thread colour — your embroidery is hand-crafted to match.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="monogram-close"
                className="p-2 text-forest/60 hover:text-forest"
                aria-label="Close"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            <div className="p-6 md:p-8 space-y-8">
              {/* Letters input */}
              <div>
                <div className="overline text-forest/60 mb-2">Letters (initials)</div>
                <input
                  type="text"
                  maxLength={6}
                  value={letters}
                  onChange={(e) => setLetters(e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase())}
                  placeholder="ABC"
                  data-testid="monogram-letters-input"
                  className="w-full bg-transparent border border-hairline rounded-lg px-4 py-3 text-2xl font-display text-forest tracking-[6px] uppercase focus:outline-none focus:border-forest"
                />
              </div>

              {/* Font grid */}
              <div>
                <div className="overline text-gold tracking-[3px] text-[10px] mb-3">§ Choose font style</div>
                <div
                  className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3"
                  data-testid="monogram-font-grid"
                >
                  {MONOGRAM_FONTS.map((f) => {
                    const selected = chosen?.id === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setChosen(f)}
                        data-testid={`monogram-font-${f.id}`}
                        className={`group relative aspect-square overflow-hidden border transition-all bg-cream/30 flex flex-col items-center justify-center ${
                          selected ? "border-gold ring-2 ring-gold" : "border-hairline hover:border-forest/60"
                        }`}
                      >
                        <span
                          className="text-4xl md:text-5xl leading-none"
                          style={{
                            fontFamily: f.family,
                            fontWeight: f.weight,
                            fontStyle: f.italic ? "italic" : "normal",
                            letterSpacing: f.spacing,
                            textTransform: f.transform,
                            color: chosenColour,
                          }}
                        >
                          {previewLetters.length <= 3 ? previewLetters : previewLetters.slice(0, 3)}
                        </span>
                        <div className="absolute inset-x-0 bottom-0 bg-white/85 px-1.5 py-1">
                          <div className="text-forest text-[9px] tracking-wider uppercase truncate">{f.label}</div>
                        </div>
                        {selected && (
                          <div className="absolute top-1.5 right-1.5 bg-gold text-forest w-5 h-5 rounded-full flex items-center justify-center">
                            <Check size={11} strokeWidth={2.5} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Thread shade chart */}
              <div>
                <div className="overline text-gold tracking-[3px] text-[10px] mb-3 flex items-center gap-1.5">
                  <Palette size={11} strokeWidth={1.8} /> § Thread shade chart
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="thread-family-tabs">
                  {families.map((fam) => (
                    <button
                      key={fam}
                      type="button"
                      onClick={() => setFamily(fam)}
                      className={`overline text-[10px] px-3 py-1 rounded-full border transition-colors ${
                        family === fam ? "bg-forest text-white border-forest" : "text-forest/70 border-hairline hover:border-forest/50"
                      }`}
                      data-testid={`thread-family-${fam.toLowerCase()}`}
                    >
                      {fam}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="thread-shade-grid">
                  {filteredShades.map((s) => {
                    const selected = thread?.code === s.code;
                    return (
                      <button
                        key={s.code}
                        type="button"
                        onClick={() => { setThread(s); setCustomThread(""); }}
                        title={`${s.code} · ${s.name}`}
                        data-testid={`thread-shade-${s.code}`}
                        className={`group border transition-all p-1.5 flex flex-col items-center gap-1 ${
                          selected ? "border-gold ring-2 ring-gold" : "border-hairline hover:border-forest/60"
                        }`}
                      >
                        <span
                          className="h-8 w-full block rounded-sm"
                          style={{ background: s.hex, border: s.hex === "#FFFFFF" ? "1px solid #E5E1D6" : "none" }}
                        />
                        <span className="text-forest text-[9px] leading-tight tracking-wider uppercase text-center truncate w-full">{s.name}</span>
                        <span className="text-forest/50 text-[9px] tabnum">{s.code}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <div className="overline text-forest/60 mb-1.5 text-[10px]">Or type a custom shade / DMC code</div>
                  <input
                    type="text"
                    value={customThread}
                    onChange={(e) => { setCustomThread(e.target.value); setThread(null); }}
                    placeholder="e.g. DMC 3853 · Pantone 18-1663 · Warm Copper"
                    className="w-full bg-transparent border border-hairline rounded-lg px-3 py-2 text-sm text-forest focus:outline-none focus:border-forest"
                    data-testid="thread-custom-input"
                  />
                </div>
              </div>

              {/* Live large preview */}
              {chosen && (
                <div className="border border-hairline bg-cream/40 p-6 md:p-10 text-center" data-testid="monogram-live-preview">
                  <div className="overline text-forest/50 text-[10px] mb-3">Live preview</div>
                  <div
                    className="text-6xl md:text-8xl leading-none"
                    style={{
                      fontFamily: chosen.family,
                      fontWeight: chosen.weight,
                      fontStyle: chosen.italic ? "italic" : "normal",
                      letterSpacing: chosen.spacing,
                      textTransform: chosen.transform,
                      color: chosenColour,
                    }}
                  >
                    {previewLetters}
                  </div>
                  <div className="mt-4 text-forest/70 text-xs">
                    {chosen.label}
                    {(thread || customThread) && <> · <span className="font-display text-forest">{customThread || `${thread.name} (${thread.code})`}</span></>}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <footer className="sticky bottom-0 bg-white border-t border-hairline px-6 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-forest/50 text-xs">Preview is indicative — final embroidery is hand-crafted by our team to match your fabric.</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="overline text-forest border border-hairline rounded-full px-4 py-2 hover:border-forest"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={!chosen || (!thread && !customThread.trim())}
                  data-testid="monogram-confirm"
                  className="overline text-white bg-forest rounded-full px-5 py-2.5 tracking-[3px] disabled:opacity-40"
                >
                  Use this monogram
                </button>
              </div>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MonogramPreviewModal;
